/**
 * Single source of truth for "can this invoice object show a PDF, and
 * what identifies it to the PDF endpoint" -- shared by InvoicePdfButton
 * (the actual PDF-fetching control) and reconciliationArtifact.js (the
 * Bank Transactions "Reconciled By" chip, which must decide PDF-vs-
 * detail-view WITHOUT rendering the button itself).
 *
 * Handles two data shapes:
 *   - Main invoices collection: has id, source, invoice_number
 *   - Raw bros_invoices collection: has bros_id, no source, no id
 */

/**
 * Detect whether this invoice object is from a source that can render a
 * PDF. 'none' covers both "no invoice" and "no known artifact".
 */
export function detectPdfSource(invoice) {
  if (!invoice) return 'none';
  if (invoice.file_url) return 'file_url';
  if (invoice.source === 'xero' && invoice.external_id && invoice.xero_tenant_id) return 'xero_direct';
  if (invoice.source === 'bros') return 'bros';
  if (invoice.bros_id && !invoice.source) return 'bros_raw';
  if (invoice.attachments?.length > 0) return 'gmail';
  // Body-only receipts (Airbnb/Guesty trip receipts): no attachment and no
  // file_url, but the backend typesets the stored email body on demand.
  if (invoice.email_body || invoice.has_email_body) return 'email_body';
  return 'none';
}

/**
 * Resolve the best identifier for the PDF endpoint URL.
 * Main invoices have `id` (UUID). Raw bros_invoices have `bros_id`.
 * The PDF endpoint accepts both via _resolve_invoice().
 */
export function resolveInvoiceKey(invoice) {
  return invoice?.id || invoice?.bros_id || null;
}

/** True only when detectPdfSource resolves to an artifact InvoicePdfButton
 * can actually fetch (mirrors InvoicePdfButton's own isEnabled gate --
 * every pdfSource branch there additionally requires a resolvable key). */
export function hasPdfArtifact(invoice) {
  const pdfSource = detectPdfSource(invoice);
  const invoiceKey = resolveInvoiceKey(invoice);
  return (
    pdfSource === 'file_url' ||
    (pdfSource === 'xero_direct' && !!invoiceKey) ||
    (pdfSource === 'bros' && !!invoiceKey) ||
    (pdfSource === 'bros_raw' && !!invoiceKey) ||
    (pdfSource === 'gmail' && !!invoiceKey) ||
    (pdfSource === 'email_body' && !!invoiceKey)
  );
}
